// @ts-nocheck
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle, 
  Download, 
  Eye, 
  Shield, 
  Clock, 
  User, 
  Smartphone,
  Globe,
  FileText,
  QrCode,
  Mail
} from 'lucide-react';
import { SignatureDocument, SignatureAssinatura } from '@/types/signature';
import { formatDate } from '@/utils/signatureUtils';

interface SignatureReportProps {
  documento: SignatureDocument;
  assinatura: SignatureAssinatura;
  qrCodeUrl?: string;
  onDownloadReport?: () => void;
}

export const SignatureReport: React.FC<SignatureReportProps> = ({ 
  documento, 
  assinatura, 
  qrCodeUrl,
  onDownloadReport
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assinado':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'expirado':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Relatório de Assinaturas</h1>
        </div>
        <p className="text-gray-600">
          Datas e horários em UTC-0300 (America/Sao_Paulo)
        </p>
        <p className="text-sm text-gray-500">
          Última atualização em {formatDate(assinatura.data_assinatura)}
        </p>
      </div>

      {/* Informações do Documento */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Informações do Documento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <Badge className={`mt-1 ${getStatusColor(documento.status)}`}>
                <CheckCircle className="w-3 h-3 mr-1" />
                {documento.status === 'assinado' ? 'Assinado' : documento.status}
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Documento</p>
              <p className="font-semibold">{documento.titulo}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Número</p>
              <p className="font-mono text-sm">{documento.id}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Data da criação</p>
              <p>{formatDate(documento.data_criacao)}</p>
            </div>
            {assinatura.hash_pdf && (
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Hash do documento original (SHA256)</p>
                <p className="font-mono text-xs break-all">{assinatura.hash_pdf}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* QR Code */}
      {qrCodeUrl && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <QrCode className="w-5 h-5 mr-2" />
              Código QR para Verificação
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <img 
              src={qrCodeUrl} 
              alt="QR Code para verificação" 
              className="mx-auto w-32 h-32"
            />
            <p className="text-sm text-gray-600 mt-2">
              Escaneie para verificar a autenticidade
            </p>
          </CardContent>
        </Card>
      )}

      {/* Assinaturas */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <User className="w-5 h-5 mr-2" />
              Assinaturas
            </span>
            <Badge variant="outline">1 de 1 Assinaturas</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Assinatura Individual */}
            <div className="border rounded-lg p-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span className="font-semibold">Assinado via Sistema de Assinatura Eletrônica</span>
                </div>
                <Badge className="bg-green-100 text-green-800 border-green-200">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Assinado
                </Badge>
              </div>

              {/* Informações do Assinante */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Nome do Assinante</p>
                  <p className="font-semibold">{assinatura.nome_assinante}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Data e hora da assinatura</p>
                  <p>{formatDate(assinatura.data_assinatura)}</p>
                </div>
                {assinatura.token_confirmacao && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Token</p>
                    <p className="font-mono text-xs">{assinatura.token_confirmacao}</p>
                  </div>
                )}
              </div>

              {/* Pontos de Autenticação */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-500 mb-2">Pontos de autenticação:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {assinatura.email_assinante && (
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      <span>E-mail: {assinatura.email_assinante}</span>
                    </div>
                  )}
                  {assinatura.ip_address && (
                    <div className="flex items-center">
                      <Globe className="w-4 h-4 mr-2 text-gray-400" />
                      <span>IP: {assinatura.ip_address}</span>
                    </div>
                  )}
                  {assinatura.user_agent && (
                    <div className="flex items-center md:col-span-2">
                      <Smartphone className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-xs">Dispositivo: {assinatura.user_agent}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Assinatura Visual */}
              {assinatura.assinatura_base64 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">Assinatura:</p>
                  <div className="border rounded p-3 bg-gray-50">
                    <img 
                      src={assinatura.assinatura_base64} 
                      alt="Assinatura" 
                      className="max-w-xs mx-auto"
                    />
                    <p className="text-center font-semibold mt-2">{assinatura.nome_assinante}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer Legal */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center">
                <Shield className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-semibold">INTEGRIDADE CERTIFICADA</span>
              </div>
            </div>
            
            <p className="text-sm text-gray-600">
              Assinaturas eletrônicas e físicas têm igual validade legal, conforme MP 2.200-2/2001 e Lei 14.063/2020.
            </p>
            
            <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
              <span>ICP Brasil</span>
              <span>ISO 27001</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Botões de Ação */}
      <div className="flex justify-center space-x-4">
        <Button variant="outline" onClick={() => window.print()}>
          <Eye className="w-4 h-4 mr-2" />
          Imprimir Relatório
        </Button>
        {onDownloadReport && (
          <Button variant="outline" onClick={onDownloadReport}>
            <Download className="w-4 h-4 mr-2" />
            Baixar Relatório Completo
          </Button>
        )}
        {documento.url_pdf_original && (
          <Button variant="outline" onClick={() => window.open(documento.url_pdf_original, '_blank')}>
            <Download className="w-4 h-4 mr-2" />
            Baixar Documento Original
          </Button>
        )}
      </div>
    </div>
  );
};
